import Model, { encoders, ID, Integer, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Librariable from 'src/models/model/concerns/Librariable'
import Page from 'src/models/page/Page'
import { deletePrefix } from 'src/router/utils'

export function groupByCategory (menuItems) {
  const hasCategory = _.filter(menuItems, 'category')
  const noCategory = _.difference(menuItems, hasCategory)

  const categories = _(hasCategory).groupBy('category').map((items, category) => { return { items, category } })

  return _.sortBy([...noCategory, ...categories], entry => entry.items ? entry.items[0].displayOrder : entry.displayOrder)
}

/**
   * Return the URL string with any current URL query parameters appended to it. This is similar
   * to the helper function in mh-helper.js
   */
function appendCurrentQueryParams (url) {
  if (!_.isString(url)) return url

  const [baseUrl, urlQueryString] = _.split(url, '?')
  const mergedQueryParams = new URLSearchParams(urlQueryString)
  const currentQueryParams = new URLSearchParams(window.location.search)

  currentQueryParams.forEach((value, key) => {
    mergedQueryParams.set(key, value)
  })

  return `${baseUrl}?${mergedQueryParams.toString()}`
}

export function isUrlSameHostName (url, compareTo = window.location.hostname) {
  if (!_.isString(url)) return false
  if (_.isEmpty(url)) return false

  if (_.startsWith(url, '/')) return true

  try {
    const parsedUrl = new URL(url)
    return parsedUrl.hostname === compareTo
  } catch (e) {
    return false
  }
}

export const DISPLAY_OPTIONS = {
  HEADER: 'header',
  MENU: 'menu',
  FOOTER: 'footer'
}

export default class Navigation extends Model {
  static name = 'Navigation'

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.SiteContent]

  static define () {
    this.concerns = [Archivable, Librariable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      title: {
        type: String,
        required: true,
        translatable: true
      },
      category: {
        type: String,
        translatable: true,
        required: true
      },
      page: {
        type: Page,
        reference: true,
        encode: encoders.hasOne,
        default: null
      },
      redirect: {
        type: Boolean,
        default: false
      },
      redirectUrl: {
        type: String
      },
      openNewWindow: {
        type: Boolean,
        default: false
      },
      showInHeader: {
        type: Boolean,
        default: false
      },
      showInMenu: {
        type: Boolean,
        default: true
      },
      showInFooter: {
        type: Boolean,
        default: true
      },
      showInMobileMenu: {
        type: Boolean,
        default: false
      },
      showInMobileApp: {
        type: Boolean,
        default: false
      },
      mobileMenuLabel: {
        type: String,
        default: null,
        translatable: true
      },
      mobileMenuIcon: {
        type: String,
        default: null
      },
      displayOrder: {
        type: Integer,
        required: true,
        default: 100
      }
    }

    this.computed = {
      hasDestination () {
        return (this.redirect && !_.isEmpty(this.redirectUrl)) || (this.page?.visible && !_.isEmpty(this.page?.slug))
      },
      visible () {
        return this.isUnarchived && this.hasDestination
      }
    }

    this.paginate = true
  }

  get name () {
    return this.title
  }

  get route () {
    return this.page?.slug ? `/${deletePrefix(this.page.slug, '/')}` : '/'
  }

  get finalRoute () {
    if (!this.redirect || !this.redirectUrl) return this.route

    let url = this.redirectUrl

    // If this is an internal link, we can remove the hostname
    url = url.replace(this.configuration.currentSite.hostname, '')

    if (_.startsWith(url, 'http')) {
      return url
    } else {
      // Routing within our site
      if (_.startsWith(url, '/#/')) url = url.substr(2)

      if (_.startsWith(url, '#/')) url = url.substr(1)

      return url
    }
  }

  // TODO: most uses of this method should be replaced with 'router-link' or 'a' components instead.
  navigateToRedirect (router, ios = false) {
    // When navigating between sites within an app, we need to append ?ios to
    // external urls so that we don't lose our ios cookie. This is a bit lazy
    // and doesn't take anything about the url into account, so will need to be
    // replaced with a more sophisticated url check. It's fine for now.
    let url = ios && this.redirect && !this.openNewWindow ? `${this.finalRoute}?ios` : this.finalRoute

    if (_.startsWith(url, 'http')) {
      if (!isUrlSameHostName(url)) {
        url = appendCurrentQueryParams(url)
      }

      if (this.openNewWindow) {
        window.open(url, '_blank')
        router.back()
      } else if (isUrlSameHostName(url)) {
        const parsedUrl = new URL(url)
        router.push(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`)
      } else {
        window.location = url
      }
    } else {
      router.push(url)
    }
  }

  get displayIcons () {
    const icons = []

    if (!this.visible) {
      icons.push({
        name: 'toggle_off',
        color: 'negative',
        tooltip: 'Not Visible (due to associated page being unavailable)',
        size: 'md'
      })
      return icons
    }

    if (this.showInHeader) {
      icons.push({
        name: 'web_asset',
        color: 'dark',
        tooltip: 'Show in Header'
      })
    }

    if (this.showInFooter) {
      icons.push({
        name: 'border_bottom',
        color: 'dark',
        tooltip: 'Show in Footer'
      })
    }

    if (this.showInMenu) {
      icons.push({
        name: 'menu_open',
        color: 'dark',
        tooltip: 'Show in Side Menu'
      })
    }

    if (this.showInMobileMenu) {
      icons.push({
        name: 'smartphone',
        color: 'dark',
        tooltip: 'Show as Mobile Menu'
      })
    }

    return icons
  }
}
